import {
    AfterContentInit,
    Component,
    ContentChildren,
    EventEmitter,
    Injector,
    Input,
    Output,
    QueryList,
    Renderer2,
    TemplateRef,
    ViewChild
} from "@angular/core";
import { AbstractControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors } from "@angular/forms";
import { AppOnlineStateService } from "@main/pwa/app-online-state.service";
import { ControlBase } from "./control-base";
import { PrimeTemplate } from "primeng/api";
import { Calendar } from "primeng/calendar";
import { Time } from "@angular/common";

@Component({
    selector: "m-parent-restricted-select-date",
    template: `
        <div *ngIf="showValue; else buttons_only" class="p-inputgroup">
            <p-calendar
                #datePicker
                [(ngModel)]="date"
                (ngModelChange)="refreshDate($event)"
                [readonlyInput]="!allowTyping"
                [showOnFocus]="false"
                [maxDate]="maxDate"
                [minDate]="minDate"
                keepInvalid="true"
                appendTo="body"
                (onInput)="onInput($event)"
                (onBlur)="onTouched()"
                [inputStyleClass]="'__date-input ' + klass">
                <ng-template pTemplate="header">
                    <ng-container *ngTemplateOutlet="calendarHeaderTmpl"></ng-container>
                </ng-template>
                <ng-template pTemplate="date" let-date>
                    <ng-container *ngIf="!calendarDateTmpl && !parentDate">{{ date.day }}</ng-container>
                    <ng-container *ngIf="!calendarDateTmpl && parentDate">
                        <span *ngIf="_isParentDate(date)" class="parent-date">{{ date.day }}</span>
                        <span *ngIf="!_isParentDate(date)">{{ date.day }}</span>
                    </ng-container>
                    <ng-container *ngTemplateOutlet="calendarDateTmpl; context: { $implicit: date }"> </ng-container>
                </ng-template>
            </p-calendar>
            <span class="p-buttonset m-buttonset-rounded">
                <p-button
                    allowOffline
                    label="Today"
                    styleClass="__today-button-aside"
                    [disabled]="_todayDisabled"
                    (onClick)="_selectToday()"></p-button>
                <p-button
                    allowOffline
                    iconPos="right"
                    icon="pi pi-calendar"
                    [disabled]="_isParentDisabled"
                    (onClick)="datePicker.toggle()"
                    class="calendar-button"></p-button>
            </span>
        </div>
        <ng-template #buttons_only>
            <span class="p-buttonset m-buttonset-rounded">
                <p-button
                    allowOffline
                    label="Today"
                    styleClass="__today-button"
                    [disabled]="_todayDisabled"
                    (onClick)="_selectToday()"></p-button>

                <p-button
                    allowOffline
                    iconPos="right"
                    icon="pi pi-calendar"
                    [disabled]="_isParentDisabled"
                    (onClick)="datePicker.toggle()"
                    class="calendar-button"></p-button>

                <p-calendar
                    #datePicker
                    styleClass="__m-select-date-picker"
                    [ngModel]="date"
                    (ngModelChange)="refreshDate($event)"
                    appendTo="body"
                    [readonlyInput]="true"
                    [showOnFocus]="false"
                    [maxDate]="maxDate"
                    [minDate]="minDate">
                    <ng-template pTemplate="header">
                        <ng-container *ngTemplateOutlet="calendarHeaderTmpl"></ng-container>
                    </ng-template>
                    <ng-template pTemplate="date" let-date>
                        <ng-container *ngIf="!calendarDateTmpl && !parentDate">{{ date.day }}</ng-container>
                        <ng-container *ngIf="!calendarDateTmpl && parentDate">
                            <span *ngIf="_isParentDate(date)" class="parent-date">{{ date.day }}</span>
                            <span *ngIf="!_isParentDate(date)">{{ date.day }}</span>
                        </ng-container>
                        <ng-container *ngTemplateOutlet="calendarDateTmpl; context: { $implicit: date }"> </ng-container>
                    </ng-template>
                </p-calendar>
            </span>
        </ng-template>
    `,
    styleUrls: ["./parent-restricted-select-date-control.component.scss"],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: ParentRestrictedSelectDateControlComponent,
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: ParentRestrictedSelectDateControlComponent,
            multi: true
        }
    ]
})
export class ParentRestrictedSelectDateControlComponent extends ControlBase<Date> implements AfterContentInit {
    date = new Date();

    @Input() set disabled(disabled: boolean) {
        this.setDisabledState(disabled);
    }

    @Input() allowOffline: boolean;
    @Input() showValue: boolean = false;
    @Input() allowTyping: boolean = false;
    @Input() maxDate: Date = null;
    @Input() minDate: Date = null;
    @Input() parentDate: Date = null;

    @Output() readonly onDateChange = new EventEmitter<Date>();

    calendarHeaderTmpl: TemplateRef<any>;
    calendarDateTmpl: TemplateRef<any>;

    @ContentChildren(PrimeTemplate) _contentTemplates: QueryList<PrimeTemplate>;

    defaultTime: Time;

    /* @ViewChild(Calendar) set dateCalendar(c: Calendar) {
        this.renderer.setAttribute(c.inputfieldViewChild.nativeElement, "inputmode", "numeric");
    } */

    @ViewChild(Calendar) dateCalendar: Calendar;

    constructor(private appOnlineService: AppOnlineStateService, private renderer: Renderer2) {
        super();
    }

    refreshDate(date: Date) {
        if (date instanceof Date) {
            date.setHours(this.defaultTime.hours);
            date.setMinutes(this.defaultTime.minutes);
        }
        this.date = date;
        this.onDateChange.emit(date);
        this.onChange(this.date);
    }

    writeValue(obj: Date): void {
        this.date = obj;

        if (obj) {
            this.defaultTime = {
                hours: obj.getHours(),
                minutes: obj.getMinutes()
            };
        }
    }

    get _todayDisabled(): boolean {
        if (this._isParentDisabled) {
            return true;
        }
        if (this.maxDate) {
            var today = new Date();
            today.setHours(0, 0, 0, 0);
            return today.getTime() > this.maxDate.getTime();
        }
        return false;
    }

    _selectToday() {
        if (this.maxDate) {
            let today = new Date();
            today.setHours(0, 0, 0, 0);
            if (today.getTime() <= this.maxDate.getTime()) {
                this.refreshDate(new Date());
            }
        } else {
            this.refreshDate(new Date());
        }
    }

    get _isParentDisabled() {
        return this.disabled || (!this.appOnlineService.isAppOnline && this.allowOffline == null);
    }

    ngAfterContentInit(): void {
        this._contentTemplates.forEach(pTmpl => {
            switch (pTmpl.getType()) {
                case "calendarHeader":
                    this.calendarHeaderTmpl = pTmpl.template;
                    break;
                case "calendarDate":
                    this.calendarDateTmpl = pTmpl.template;
                    break;
            }
        });
    }

    private _isParentDate(date: { day: number; month: number; year: number; today: boolean; selectable: boolean }) {
        if (this.parentDate) {
            if (
                date.day === this.parentDate.getDate() &&
                date.month === this.parentDate.getMonth() &&
                date.year === this.parentDate.getFullYear()
            ) {
                return true;
            }
            return false;
        }
        return false;
    }

    validate(control: AbstractControl): ValidationErrors | null {
        if (control.value instanceof Date) {
            return null;
        }
        return { invalidDate: true };
    }

    onInput(event: any) {
        try {
            let value = this.dateCalendar.parseValueFromString(event.target.value);
            if (!this.dateCalendar.isValidSelection(value)) {
                this.dateCalendar.updateModel(event.target.value);
            }
        } catch (error) {
            this.dateCalendar.updateModel(event.target.value);
        }
    }
}
